import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PlayCircle, Shield, Briefcase, Presentation, FileText, X, ArrowRight, Sparkles } from "lucide-react";
import { setDemoMode } from "../lib/links";

/**
 * Hero-mounted "FMO Mode" CTA — always visible.
 * Click → opens a centered modal with 4 one-click entry points into the platform:
 *   - FMO Admin Dashboard (auto-signs in)
 *   - Agent Dashboard (auto-signs in)
 *   - Pitch Deck (full slide presentation)
 *   - Live client form (with autofill + scenarios)
 *
 * Auto-open the modal by visiting any URL with ?launch=1 (e.g. footer link).
 */
export default function InvestorDemoButton() {
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    // Auto-open the launcher when the URL says ?launch=1
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("launch") === "1") {
            setOpen(true);
            params.delete("launch");
            const qs = params.toString();
            navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
        }
    }, [location.pathname, location.search, navigate]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    const tours = [
        {
            id: "admin",
            icon: Shield,
            title: "FMO Admin Dashboard",
            desc: "The control center · 80+ live leads, agent roster, conversion stats, commission tracking, white-label branding",
            href: "/admin/login?prefill=admin",
            tone: "from-navy-900 to-navy-800",
            accent: "text-amber-300",
            badge: "Most impressive",
        },
        {
            id: "agent",
            icon: Briefcase,
            title: "Agent Dashboard",
            desc: "What individual agents see · their leads only, profile editor, completeness meter, status updates",
            href: "/admin/login?prefill=agent",
            tone: "from-emerald-800 to-emerald-700",
            accent: "text-emerald-200",
        },
        {
            id: "pitch",
            icon: Presentation,
            title: "FMO Pitch Deck",
            desc: "15-slide presentation · the business case, economics, social strategy, the ask. Press → to advance",
            href: "/pitch",
            tone: "from-amber-700 to-amber-600",
            accent: "text-amber-100",
        },
        {
            id: "form",
            icon: FileText,
            title: "Client Review Form",
            desc: "The lead-capture wizard with built-in scenarios · autofill demo data instantly · demo submissions are never stored as real leads",
            href: "/review-demo",
            tone: "from-sky-800 to-sky-700",
            accent: "text-sky-200",
        },
    ];

    const launch = (href) => {
        // Turn on demo mode so the bottom-left Demo Mode pill + Cmd+K launcher show up
        // on every subsequent page the prospect visits.
        setDemoMode(true);
        setOpen(false);
        navigate(href);
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid="investor-demo-cta"
                className="group inline-flex items-center gap-2.5 bg-white/5 backdrop-blur border-2 border-amber-300/60 text-amber-200 hover:text-amber-100 hover:border-amber-300 hover:bg-amber-400/10 px-6 py-7 text-base font-semibold rounded-md transition-all whitespace-nowrap w-full sm:w-auto justify-center"
            >
                <PlayCircle className="w-5 h-5 text-amber-300 group-hover:scale-110 transition-transform shrink-0" />
                <span>See FMO Mode</span>
                <span className="hidden lg:inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold bg-amber-400/20 text-amber-200 px-2 py-0.5 rounded-full border border-amber-300/40">
                    <Sparkles className="w-3 h-3" /> Demo only
                </span>
            </button>

            {open && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 animate-[fadeIn_.15s_ease-out]"
                    role="dialog"
                    aria-modal="true"
                    data-testid="investor-demo-modal"
                    onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
                >
                    <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" />
                    <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden max-h-[90vh] overflow-y-auto">
                        <div className="bg-gradient-to-r from-amber-400 via-amber-300 to-amber-400 text-slate-900 px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <PlayCircle className="w-5 h-5" />
                                <div>
                                    <div className="font-serif text-lg leading-none">FMO Mode Launcher</div>
                                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold mt-1 opacity-80">One click into a live, working surface</div>
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                className="rounded-full p-1.5 hover:bg-slate-900/10 transition"
                                data-testid="investor-demo-close"
                                aria-label="Close demo launcher"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {tours.map((t) => {
                                const Icon = t.icon;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => launch(t.href)}
                                        data-testid={`investor-demo-${t.id}`}
                                        className={`group text-left bg-gradient-to-br ${t.tone} text-white rounded-xl p-5 hover:scale-[1.02] hover:shadow-2xl transition-all relative overflow-hidden`}
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <div className={`w-10 h-10 rounded-md bg-white/10 flex items-center justify-center ${t.accent}`}>
                                                <Icon className="w-5 h-5" />
                                            </div>
                                            {t.badge && (
                                                <span className="text-[9px] font-bold uppercase tracking-[0.15em] bg-amber-400 text-slate-900 px-2 py-0.5 rounded-full">
                                                    {t.badge}
                                                </span>
                                            )}
                                        </div>
                                        <div className="font-serif text-lg leading-tight">{t.title}</div>
                                        <div className="mt-2 text-sm text-white/75 leading-snug">{t.desc}</div>
                                        <div className={`mt-4 inline-flex items-center gap-1.5 text-xs font-semibold ${t.accent} group-hover:gap-2.5 transition-all`}>
                                            Launch <ArrowRight className="w-3.5 h-3.5" />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="bg-slate-50 border-t border-slate-200 px-6 py-3 flex items-center justify-between text-xs text-slate-700">
                            <span><kbd className="bg-white border border-slate-300 rounded px-1.5 py-0.5 text-[10px] font-mono">⌘K</kbd> opens this anywhere · <kbd className="bg-white border border-slate-300 rounded px-1.5 py-0.5 text-[10px] font-mono">Esc</kbd> closes</span>
                            <span className="font-semibold text-slate-600">Demo only · invisible to real clients</span>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>
        </>
    );
}
